import { PortalQuoteResponseType } from 'stringline-types';
import { formatDate } from '../lib/format';

type QuoteData = PortalQuoteResponseType['data'];

interface QuoteHeaderProps {
  company: QuoteData['company'];
  quote: QuoteData['quote'];
}

const QuoteHeader = ({ company, quote }: QuoteHeaderProps) => {
  const idLabel = company.nationalIdentifier
    ? company.nationalIdentifierType
      ? `${company.nationalIdentifierType.toUpperCase()} ${company.nationalIdentifier}`
      : company.nationalIdentifier
    : null;

  return (
    <header className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-4">
        {company.logoUrl ? (
          <img
            src={company.logoUrl}
            alt={`${company.name} logo`}
            className="h-16 w-16 shrink-0 rounded-md border border-slate-200 bg-white object-contain p-1"
          />
        ) : (
          <div
            aria-hidden="true"
            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-100 text-lg font-semibold text-slate-500"
          >
            {company.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{company.name}</h1>
          {idLabel ? <p className="mt-1 text-sm text-slate-600">{idLabel}</p> : null}
          {company.registeredForTax ? (
            <p className="mt-0.5 text-xs text-slate-500">Registered for GST</p>
          ) : null}
        </div>
      </div>

      <div className="text-sm text-slate-700 sm:text-right">
        <p className="text-base font-semibold text-slate-900">Quote #{quote.number}</p>
        {quote.name ? <p className="mt-0.5 text-slate-600">{quote.name}</p> : null}
        <dl className="mt-2 space-y-0.5">
          {quote.issuedAt ? (
            <div className="flex gap-2 sm:justify-end">
              <dt className="text-slate-500">Issued:</dt>
              <dd>{formatDate(quote.issuedAt)}</dd>
            </div>
          ) : null}
          {quote.dueDate ? (
            <div className="flex gap-2 sm:justify-end">
              <dt className="text-slate-500">Due:</dt>
              <dd>{formatDate(quote.dueDate)}</dd>
            </div>
          ) : null}
        </dl>
        {quote.mainAddressDisplay ? (
          <p className="mt-3 max-w-xs whitespace-pre-line text-slate-600 sm:ml-auto">
            {quote.mainAddressDisplay}
          </p>
        ) : null}
      </div>
    </header>
  );
};

export default QuoteHeader;
